// AI story cleanup prompt generation

// System prompt for all polish calls — replaces the Lambda's generic conversational framing
export const POLISH_SYSTEM_PROMPT =
  'You are a warm, thoughtful guide helping people preserve their life stories and memories for their loved ones. ' +
  'Your role is to help users reflect deeply and write meaningful stories. ' +
  'Be empathetic, encouraging. ' +
  'Always write in the SAME LANGUAGE the person wrote in. If their text is in Spanish, write in Spanish; ' +
  'if French, write in French. Never translate their words into English or any other language. ' +
  'Your entire response is the story text — nothing else. ' +
  'No preamble. No notes. No explanation of changes. ' +
  'Never address the reader, ask for more content, or describe your own task or process. ' +
  'Never write phrases like "please share", "I\'m waiting", "I\'ll craft", "I\'m ready to help", ' +
  '"the actual response", or any similar meta-commentary. ' +
  'If the material is sparse, write a shorter piece. A short memory is a complete memory. ' +
  'A single word of commentary would ruin a printed book page.';

// Conservative language guard. Haiku occasionally writes the title (or whole story) in the
// wrong language — e.g. an English interview titled "Mi Primer Sueldo". When the source text
// is confidently English (the overwhelming common case), we tell the model to write in English
// explicitly. For anything not confidently English we return '' and let the model's own
// language detection stand, so genuine multilingual stories (TB-097) are untouched.
export const ENGLISH_MARKERS = new Set([
  'the', 'and', 'was', 'were', 'a', 'an', 'to', 'of', 'my', 'i', 'it', 'that', 'this', 'in',
  'on', 'for', 'with', 'when', 'what', 'he', 'she', 'they', 'we', 'you', 'is', 'are', 'had',
  'have', 'at', 'but', 'so', 'as', 'me', 'her', 'his', 'our', 'their', 'about', 'just', 'because',
]);
export function englishLanguageDirective(text) {
  // Include accented letters so Spanish words stay WHOLE. With the plain [a-z'] class, "vivía" split
  // into "viv"+"a" and the stray "a" matched the English marker 'a' — a false-positive English force
  // on short Spanish once the length floor dropped. Keeping accents intact fixes it.
  const words = (text || '').toLowerCase().match(/[a-záéíóúñ']+/g) || [];
  // PIPE-LANG-1 belt (2026-07-10): the old `< 8` floor left short English input (e.g. the 6-word
  // grief incident "My mother passed away last spring") with NO directive, so the polish was free to
  // flip languages. Lowered to `< 4` — the density check below still distinguishes short English from
  // short Spanish (Spanish hits ~0% of these function words), and the PIPE-LANG-1 output gate backstops
  // anything this misses. Directive-side prevention; the gate is the enforcement.
  if (words.length < 4) return ''; // below 4 words there is no reliable density signal
  let hits = 0;
  for (const w of words) { if (ENGLISH_MARKERS.has(w)) hits++; }
  const density = hits / words.length;
  // English prose runs ~20-30% function words; Spanish/French rarely hit even 8% of THESE words.
  return density >= 0.12
    ? '\n- The person wrote in English. Write the title AND the story in English. Do not use any other language.'
    : '';
}

// Grief keepsake guard: a grief interview's real-time restraint (Pip never validates self-blame)
// must also reach the SAVED artifact. The polish preserves every detail by default, which would
// keep the storyteller's self-blame and any not-wanting-to-live thoughts and print them on a page
// the family reads. When the memory is grief, omit those.
// Gated on a narrow DEATH/LOSS-of-a-person signal (not the broad hard-memory keywords), so an
// ordinary "we had an accident, it was my fault, but we laughed" story keeps its words intact.
// DUPLICATED from interviewEngine.js (DEATH_LOSS_RE) — kept byte-identical; guarded by the drift
// test in interviewEngine.griefTrigger.test.js. BACKLOG: dedupe into one voice-safe shared module.
const DEATH_PERSON = 'mother|mom|mum|mama|momma|mommy|father|dad|daddy|papa|pop|parents|folks|son|daughter|child|children|kid|kids|baby|babies|husband|wife|spouse|partner|fiance|fiancee|brother|sister|sibling|grandmother|grandma|granny|grandfather|grandpa|grandad|grandparent|grandparents|grandkid|grandkids|grandson|granddaughter|grandchild|grandchildren|aunt|auntie|uncle|cousin|nephew|niece|friend|boyfriend|girlfriend';
const DEATH_LOSS_RE = new RegExp(
  '(' +
    '\\bdeath\\b|' +
    'passed away|passed on|passed peacefully|passed recently|passed suddenly|passed unexpectedly|passed last|passed this|' +
    'funerals?|buried|burial|gravesite|grave site|mourning|grieving|memorial service|obituary|rest in peace|eulogy|' +
    'killed (?:himself|herself|themselves|themself)|took (?:his|her|their) own life|committed suicide|suicide|overdosed|' +
    '(?:\\b(?:he|she|they|' + DEATH_PERSON + '))\\s+died(?!\\s+laughing)|' +
    '(?:\\b(?:he|she|they|' + DEATH_PERSON + '))\\s+passed(?!\\s+(?:the|my|his|her|a|an|another|it|out|by|down|through|up|away|on)\\b)|' +
    'lost (?:my|his|her|their|our) (?:' + DEATH_PERSON + ')\\b|lost (?:him|her|them)\\b|' +
    'muri[oó]|falleci\\w*|difunto|est mort|d[eé]c[eé]d\\w*' +
  ')', 'i');
const GRIEF_KEEPSAKE_NOTE = '\n\nGRIEF NOTE (this OVERRIDES the preserve-every-detail rules): This is the death or loss of a person. Keep it a warm remembrance of them and the love. Do NOT include the storyteller\'s self-blame or guilt ("it was my fault," "I wasn\'t there," "I should have been there," "I regret it") or any thoughts of not wanting to live or go on. Omit those entirely -- they must never land on a printed keepsake page a family reads.';
export function griefKeepsakeNote(text) {
  return DEATH_LOSS_RE.test(text || '') ? GRIEF_KEEPSAKE_NOTE : '';
}

// F-182 (write-your-own unification): the ONE shared polish-routing decision, extracted VERBATIM from the save
// path so the save flow AND all three re-polish handlers make the SAME isCelebration / recipientName /
// SHAPE-1-gate call. Pure (no React, no engine) so the save-path refactor is a PROVEN NO-OP (unit-tested) and no
// caller re-derives it. `isHard` is passed in (hasHardMemorySignal(text)) to keep this engine-free.
// Mirrors the save path exactly (App.js, TB-108):
//   isFirstPersonVantage = forceFirstPerson || bookAuthorVantage === 'self';
//   isCelebration        = isFirstPersonVantage ? false : !!isGiftBook;
//   SHAPE-1 plan gate: delegated to shouldRunShapePlan() -- FALSE on every route since F-364.
export function resolvePolishRouting({ isGiftBook = false, bookAuthorVantage = null, forceFirstPerson = false, recipientName = '', isHard = false } = {}) {
  const isFirstPersonVantage = !!forceFirstPerson || bookAuthorVantage === 'self';
  const isCelebration = isFirstPersonVantage ? false : !!isGiftBook;
  return {
    isCelebration,
    isFirstPersonVantage,
    recipientName: isCelebration ? (recipientName || '') : '',
    isHard: !!isHard,
    // F-364/F-365: was `!isCelebration`, which stopped being true the moment the plan pass was disabled
    // and left this field asserting a route that no longer exists. It now delegates to the ONE switch, so
    // the returned value cannot drift from what the call sites actually do.
    runShapePlan: shouldRunShapePlan({ isCelebration }),
  };
}

/**
 * F-364 — the SHAPE-1 plan pass is DISABLED on the own-story path.
 *
 * WHY, and why it is a switch rather than a deletion: the own-story Whole body was rolled back to the
 * V4 (516f71b) text, which has NO structural-plan slot. `getWholeShapePlanPrompt` and its injection
 * branch are UNCHANGED and still work; nothing calls them on this path any more. Leaving the pass
 * running would spend a Sonnet-5 call and up to 8s of latency per generation on a result the writer
 * cannot read -- on the same path where F-359 recorded a 34.1s generation and two 29s gateway severs.
 *
 * This lives here, not inlined at the App.js call sites, because App.js cannot be loaded in Jest: a
 * decision inlined there cannot be unit-tested. Same seam pattern as saveGuards.js.
 *
 * TO RE-ENABLE: flip OWN_STORY_SHAPE_PLAN_ENABLED to true. That is the whole revert -- but note the
 * V4 body ignores `context.plan`, so re-enabling only makes sense alongside a body that reads it.
 */
export const OWN_STORY_SHAPE_PLAN_ENABLED = false;

/** The ONE decision both generation call sites ask before running the plan pass. Pure, unit-tested. */
export function shouldRunShapePlan({ isCelebration = false } = {}) {
  return !isCelebration && OWN_STORY_SHAPE_PLAN_ENABLED;
}

/**
 * WHOLE-TALE-COVERAGE-UNGUARDED -- the length clause on the own-story Whole Tale body.
 *
 * THE DEFECT. The capped clause below is the only length instruction the Whole polish carries, and it
 * is a FIXED target that does not scale with the telling. On a 2,579-word assembled draft from a
 * 79-turn voice interview it measured 69%-90% topic coverage (n=9 clean production-faithful runs,
 * mean ~81%, ~1,419 words), with a catastrophic-compression tail: one observed run kept 8 of 29 topics
 * (28%) in 596 words. Nothing in the pipeline notices. `stop_reason` was `end_turn` on every run, so
 * the 4,096-token cap is NOT the constraint -- the model is obeying the clause.
 *
 * WHY A SWITCH RATHER THAN AN EDIT, and this is the whole reason the fix is not simply applied:
 * replacing the clause is measured to WORK (93%-97% coverage, 1,919-2,191 words, n=4) and measured to
 * be UNSHIPPABLE ON ITS OWN. It moves generation from 24.1s-25.8s to 29.1s-32.3s, past BOTH the 25s
 * client timer (`GENERATION_TIMEOUT_MS`, App.js) and the immutable 29s API Gateway cut -- and the Whole
 * polish leg still goes out through plain `callAi`, not `callAiResumable`. Only the assembler leg is
 * resumable today. Flipping this constant before the polish leg is resumable would convert "my story is
 * too short" into "my story failed to generate", which is a worse outcome for the same teller.
 *
 * TO ENABLE: build F-415-POLISH-RESUME (the polish call site uses `callAiResumable`, exactly as the
 * assembler call site already does), THEN flip this to true. It is Tier A -- Alex walks it before it
 * ships. The eval measures both sides today via the two exported clauses below.
 *
 * The CELEBRATION twin (`context.isCelebration`) is deliberately NOT routed through this. It is marked
 * "Do not alter" (DZ-5) and condition B was never measured against it; changing it here would be an
 * unmeasured edit to a frozen prompt riding on another prompt's evidence.
 */
export const WHOLE_TALE_LENGTH_CLAUSE_CAPPED = 'Keep it personal and authentic, around 3-5 paragraphs.';
export const WHOLE_TALE_LENGTH_CLAUSE_UNCAPPED = 'Keep it personal and authentic. Use as many paragraphs as it takes.';
export const WHOLE_TALE_UNCAPPED_LENGTH_ENABLED = false;

/**
 * The clause the own-story Whole body renders. With the flag OFF this returns the capped clause
 * VERBATIM, so the rendered prompt stays byte-identical to the pre-refactor bytes -- asserted in
 * `aiPrompts.wholeTaleLength.test.js`, because a "byte-neutral" refactor of Tier 1 prompt text is
 * only byte-neutral if something checks.
 */
export function wholeTaleLengthClause() {
  return WHOLE_TALE_UNCAPPED_LENGTH_ENABLED ? WHOLE_TALE_LENGTH_CLAUSE_UNCAPPED : WHOLE_TALE_LENGTH_CLAUSE_CAPPED;
}

export const getStoryCleanupPrompt = (text, tier, context = {}) => {
  const questionCtx = context.question ? `They were answering: "${context.question}"\n\n` : '';
  const titleCtx = context.title ? `The chapter is about: "${context.title}"\n\n` : '';

  // Story type preamble — helps Claude understand the source material
  const STORY_TYPE_PREAMBLES = {
    memoir:           'This is a personal memoir entry about the author\'s own life and experiences.\n\n',
    memory_interview: 'This text came from a spoken memory or interview. It may sound conversational, ' +
                      'fragmented, or include filler — that is expected.\n\n',
    journal:          'This is a personal journal entry. Preserve its private, reflective, first-person voice.\n\n',
    estate:           'This is an estate planning or final wishes note. Keep the tone clear and practical.\n\n',
  };
  const storyTypeCtx = context.storyType ? (STORY_TYPE_PREAMBLES[context.storyType] || '') : '';

  const OUTPUT_RULES = `
OUTPUT RULES — NON-NEGOTIABLE:
- Write in the SAME LANGUAGE as the person's text. Never translate it to English or any other language. The title must be in that language too.
- Output ONLY the title on line 1, then the story text. That is your complete response.
- Do NOT write anything before the title or after the last sentence of the story.
- Do NOT include any notes, commentary, explanations, or metadata — not a single word.
- Do NOT write: "Note:", "I kept...", "Here's the...", "Changes made:", "AI Response",
  "The input was short", "I was unable to...", or any similar preamble, label, or postscript.
- Do NOT use "##" headers, "---" dividers, asterisks, or any markdown formatting whatsoever.
- Do NOT explain what you changed, what you preserved, or what the input lacked.
- If the input is very short, output the title and the edited text — then stop. Do not comment on length.
- This text appears directly on a printed page in a family's keepsake book.
  A single line of commentary ruins that page permanently.${englishLanguageDirective(text)}${griefKeepsakeNote(text)}`;

  // TB-104 Stage 3: opt-in celebration guardrail — inject ONLY when context.isCelebration is true.
  // Callers that do not pass isCelebration are completely unaffected (existing callers remain unchanged).
  const celebrationGuardrail = context.isCelebration
    ? '\nCELEBRATION NOTE: The contributor is writing about someone else, not themselves. They can only report what they witnessed or were told. Do not infer the subject\'s feelings, personality, or private thoughts from brief descriptive language. Preserve vague impressions exactly as written.'
    : '';

  // ── 'grammar' tier — removed from UI in Session 1 (see TB-031) ──
  // Dead code. No UI button routes here. Keep for potential future use.
  // If re-exposed: add to POLISH_SYSTEM_PROMPT wiring and test thoroughly.
  if (tier === 'grammar') {
    return `${storyTypeCtx}${questionCtx}${titleCtx}Fix the grammar and spelling in this text for a printed keepsake book.

RULES:
- Fix spelling, punctuation, and grammar errors
- Remove filler words (um, uh, like, you know) if this came from speech
- IMPORTANT: Break the text into short paragraphs (2-4 sentences each). This will be printed in a book — big blocks of text are hard to read. Add a blank line between paragraphs.
- Do NOT change their words, phrasing, or sentence structure
- Do NOT add transitions, connections, or new sentences
- Do NOT rearrange or reorder anything
- Do NOT expand or add detail — if they wrote 3 sentences, return 3 sentences
- Keep it in first person
- Keep all names, places, and details exactly as stated
- Add a short title on line 1 (from their words, under 6 words) if no title exists
${OUTPUT_RULES}

The goal: identical to what they wrote, just without typos and filler, broken into readable paragraphs.

Their text:
${text}`;
  }

  if (tier === 'cleanup') {
    return `${storyTypeCtx}${questionCtx}${titleCtx}Clean up this text for a printed keepsake book.
Your job is the lightest possible hand. The reader should not be able to tell AI touched this.
Make it clean and readable — not rewritten.${celebrationGuardrail}

RULES:
- Fix grammar, spelling, and punctuation
- Smooth transitions between ideas
- Connect fragments or bullet points into flowing paragraphs
- Remove filler words from speech
- IMPORTANT: Break the text into short paragraphs (2-4 sentences each). This will be printed in a book — big blocks of text are hard to read. Add a blank line between paragraphs.
- Do NOT add emotions, thoughts, or details they didn't write
- Do NOT add literary flourishes, metaphors, or poetic language
- Do NOT significantly expand the length — keep it close to what they gave you
- Keep THEIR words and vocabulary. If they write simply, keep it simple.
- Keep it in first person
- Keep all names, places, and specific details exactly as stated
- Add a short title on line 1 (from their words, under 6 words) if no title exists

SHORT INPUT RULE: If the input is fewer than 30 words, apply only spelling and punctuation
fixes, add a title, and stop. Do not attempt to expand, pad, or restructure. A short memory
is a complete memory.
${OUTPUT_RULES}

The goal: they read it and think "that's what I said, just smoother."

Their text:
${text}`;
  }

  if (tier === 'chapter') {
    return `${storyTypeCtx}${questionCtx}${titleCtx}Shape this draft into a warm, readable story in the first person, preserving the author's voice exactly. Keep it personal and authentic, 2-4 paragraphs.${celebrationGuardrail}

FIDELITY RULE -- NON-NEGOTIABLE:
Every concrete detail the person stated must appear in the output.
"Concrete detail" means: any name (person, place, brand, song, object), any age or year,
any specific object they mentioned, any emotion they stated, any event they described.
Omitting a stated concrete detail is a failure. The family will notice what is missing.

RULES:
- Preserve the author's voice and their specific words and phrases
- Keep all names, places, dates, ages, objects, and details exactly as stated -- every single one
- Keep it in first person
- Connect rough notes into flowing paragraphs -- only using the events and details they gave you
- Expand a stated detail ONLY in ways the person's own words already support
  (e.g. "it was loud" -> "the room was loud" is allowed; inventing what the noise was is not)
- Do NOT invent facts, events, names, dialogue, sensory details, physical descriptions, weather,
  settings, or emotions and inner thoughts the person did not state
- Do NOT compress or drop details to fit a target length -- if it takes more paragraphs to
  include everything they said, use more paragraphs
- Add a short, evocative title on line 1 (drawn from their actual words)

SHORT INPUT RULE: If the input is fewer than 30 words, keep the output close to what they wrote.
Fix grammar, add a title, add paragraph breaks -- but do NOT add sensory detail, physical
description, dialogue, or scene-setting they did not write. A short memory is a complete memory.
${OUTPUT_RULES}

The goal: their family reads this and says "that sounds exactly like them -- and nothing is missing."

Their words:
"${text}"`;
  }

  return text; // fallback
};

// ---------------------------------------------------------------------------
// Interview story dual-version polish prompts
// "A Flick of the Tail" (LIGHT) and "The Whole Tale" (FULL MAGIC)
// ---------------------------------------------------------------------------

/**
 * LIGHT version: "A Flick of the Tail"
 * Tidied and readable, but unmistakably the storyteller's own words and voice.
 * Fix grammar, smooth flow, remove filler/false starts — stay very close to
 * what they actually said. Minimal elevation. No invented facts.
 *
 * Bar: "My first car was a 1996 white Pontiac Bonneville with gold rims. The
 * first place I drove it was probably to work, at Smuggler's Cove..."
 * (their phrasing, just cleaned up)
 *
 * @param {string} text        -- assembled storyteller content
 * @param {object} [context]   -- { question, isCelebration, storyType }
 * @returns {string}
 */
export const getFlickPolishPrompt = (text, context = {}) => {
  const questionCtx = context.question ? `They were answering: "${context.question}"\n\n` : '';

  // CELEBRATION / GIFT-BOOK PATH -- unchanged from the prior validated version.
  // Third-person, contributor-writing-about-someone-else framing. Do not alter.
  if (context.isCelebration) {
    const celebrationGuardrail = '\nCELEBRATION NOTE: The contributor is writing about someone else, not themselves. Preserve vague impressions exactly as written. Do not infer the subject\'s feelings, personality, or private thoughts.';

    const OUTPUT_RULES = `
OUTPUT RULES -- NON-NEGOTIABLE:
- Write in the SAME LANGUAGE as the person's text. Never translate.
- Output ONLY the title on line 1, then the story text. Nothing else.
- Do NOT write anything before the title or after the last sentence of the story.
- Do NOT include notes, commentary, explanations, or metadata of any kind.
- Do NOT use "##" headers, "---" dividers, asterisks, or markdown formatting.
- Do NOT use em-dashes (the long dash). Use commas, periods, or colons instead. Ordinary hyphens in compound words are fine.
- NEVER write the words "storyteller", "author", "contributor", or "the subject" in the title or story. The person is "I" (or, in a celebration/gift book, their name or natural pronouns: he, she, they). If the input text refers to "the storyteller", replace it the same way.
- If the input is very short, output the title and the edited text -- then stop.
- This text appears directly on a printed page in a family's keepsake book.${englishLanguageDirective(text)}`;

    return `${questionCtx}This is a storyteller's spoken or written memory for a printed keepsake book.
Your job is the lightest possible editorial hand. The goal: they read it and think "that's exactly what I said, just cleaner."${celebrationGuardrail}${griefKeepsakeNote(text)}

RULES:
- Fix grammar, spelling, punctuation, and run-on sentences
- Remove filler words (um, uh, like, you know, sort of) and false starts from speech
- FLOW MATTERS MOST: spoken memories come out as short, clipped sentences. Actively combine closely related ones into smoother, flowing sentences and vary the length, so it never reads as staccato, "period after period." Use natural connectors: and, but, so, because, when, which. For example, "I had a dog. His name was Rex. He followed me everywhere." should become "I had a dog named Rex, and he followed me everywhere." Keep every one of their words and details; only improve how the sentences connect.
- Break the text into short paragraphs (2-4 sentences each); add a blank line between paragraphs
- PRESERVE their exact vocabulary and phrasing. If they write simply, keep it simple. Reshape the flow so it reads like natural writing, not a clipped list, but do not add words that carry new meaning.
- PRESERVE every concrete detail: every name, place, brand, year, age, object, emotion they stated
- Do NOT add literary flourishes, metaphors, vivid imagery, or evocative language
- Do NOT rearrange the order of events or details
- Do NOT expand any detail beyond what they said -- a plain statement stays plain
- Do NOT invent facts, emotions, dialogue, settings, or sensory details they did not express
- Keep it in first person (or third person if this is a celebration/gift book)
- Keep the length close to what they gave you; do not pad or compress

SHORT INPUT RULE: If the input is fewer than 30 words, apply only spelling and punctuation
fixes, add a title, and stop. A short memory is a complete memory.

Add a short chapter title on line 1 (drawn from their actual words, under 7 words).
${OUTPUT_RULES}

Their words:
"${text}"`;
  }

  // OWN-STORY (first-person, non-celebration) PATH -- Flick v3 (founder-revised 2026-07-06;
  // validated on thin voice + rich transcripts). Adds an explicit connector permission so
  // short one-fact-per-line answers flow instead of reading choppy, while banning invented
  // cause/order. englishLanguageDirective is kept appended as the Spanish-flip safety net.
  return `You are helping a person turn their interview answers into a short, first-person chapter for a family keepsake book.${griefKeepsakeNote(text)}

${questionCtx}Input draft: ${text}

Rules (strict):
- Use ONLY what they actually said. Never invent or add any hard fact, number, count, name, place, person, time, event, dialogue, feeling, or detail.
- Never delete or weaken any true detail they gave.
- You may use natural connecting words (and, but, so, because, when, which, while) only to join ideas they already connected -- do not use them to imply a cause, reason, or order they did not say.
- Do not add interpretive words that put intent or feeling in their mouth.
- Keep their own phrasing and wording wherever possible.

Your job is to turn the input into smooth, natural first-person prose with varied sentence lengths so it flows without sounding robotic or list-like. On already-clean input, focus on flow and paragraphing only.

Output:
Line 1: short plain title
Then the chapter.
Same language as their words. No notes, no markdown, no em-dashes.${englishLanguageDirective(text)}`;
};

/**
 * FULL MAGIC version: "The Whole Tale"
 * Warm, evocative, well-crafted first-person memoir prose: a real opening line,
 * rhythm, emotional warmth, vivid arrangement of THEIR details.
 * Still 100% truthful -- never invent facts, events, people, places, or feelings
 * they did not express.
 *
 * Bar: "My first car was a 1996 white Pontiac Bonneville, gold rims and all.
 * I'd drive it to my job at Smuggler's Cove..."
 * (elevated writing using only their details)
 *
 * @param {string} text        -- assembled storyteller content
 * @param {object} [context]   -- { question, isCelebration, storyType }
 * @returns {string}
 */
export const getWholePolishPrompt = (text, context = {}) => {
  const questionCtx = context.question ? `They were answering: "${context.question}"\n\n` : '';

  // CELEBRATION / GIFT-BOOK PATH -- unchanged from the prior validated version.
  // Third-person, contributor-writing-about-someone-else framing. Do not alter.
  if (context.isCelebration) {
    const celebrationGuardrail = '\nCELEBRATION NOTE: The contributor is writing about someone else, not themselves. They can only report what they witnessed or were told. Do not infer the subject\'s feelings, personality, or private thoughts from brief descriptive language. Preserve vague impressions exactly as written.';

    const OUTPUT_RULES = `
OUTPUT RULES -- NON-NEGOTIABLE:
- Write in the SAME LANGUAGE as the person's text. Never translate.
- Output ONLY the title on line 1, then the story text. Nothing else.
- Do NOT write anything before the title or after the last sentence of the story.
- Do NOT include notes, commentary, explanations, or metadata of any kind.
- Do NOT use "##" headers, "---" dividers, asterisks, or markdown formatting.
- Do NOT use em-dashes (the long dash). Use commas, periods, or colons instead. Ordinary hyphens in compound words are fine.
- No spoken scaffolding may survive in the output: never print phrases like "one specific thing that stands out," "um," "basically," "and then," "so yeah," or "pretty much."
- NEVER write the words "storyteller", "author", "contributor", or "the subject" in the title or story. The person is "I" (or, in a celebration/gift book, their name or natural pronouns: he, she, they). If the input text refers to "the storyteller", replace it the same way.
- If the input is very short, keep the output close to what they wrote -- fix grammar, add a title, but do not add invented detail.
- This text appears directly on a printed page in a family's keepsake book.${englishLanguageDirective(text)}`;

    return `${questionCtx}Please expand this rough draft into a warm, meaningful story in the first person, preserving the author's voice. Keep it personal and authentic, around 3-5 paragraphs.${celebrationGuardrail}${griefKeepsakeNote(text)}

RULES:
- Preserve the author's voice and their specific words and phrases
- Keep all names, places, dates, and details exactly as stated
- Keep it in first person (third person for celebration/gift books)
- It's OK to expand rough notes into full, flowing paragraphs
- It's OK to add warmth, sensory detail, and gentle narrative structure
- It's OK to flesh out ideas they hinted at
- Do NOT invent facts, events, names, or dialogue they didn't mention
- Write in THEIR spoken voice: plain words this person could actually have said out loud. Do NOT add metaphors, poetic imagery, or reflective "wisdom" observations that are not theirs. If they do not speak in metaphor, the story does not either.
- Keep their most alive, personal phrases word-for-word. Never trade a vivid line of theirs for a smoother, more generic one.
- If they left a feeling, question, or hope unresolved, the story leaves it unresolved. Do not upgrade "I wondered if I could" into a lesson learned or a neat, triumphant ending.
- Never state a number, count, time, or amount they did not state. "My sisters" stays "my sisters", never "the three of us". "We played outside" does not become "we played outside until dark".
- FLOW MATTERS: spoken memories come out as short, clipped sentences. Combine closely related ones into smoother sentences of varied length using natural connectors (and, but, so, because, when). Keep their words; fix the flow.
- Their spoken generic "you" becomes first person: "your heart was racing" is written as "my heart was racing," "if you put your feet against the door" becomes "I would put my feet against the door." Keep the phrase, fix the pronoun.
- Find the story's turn before you write. It is often the line that undercuts or ends everything else they said ("I always got caught"), and often the first or funniest thing they said. NEVER drop that line: build the ending on it. BUT a flat sign-off ("Great." / "That's it" / "Yeah") is NOT the turn: dissolve it and end on their strongest material instead. If they described what usually happened rather than one specific time, keep it honestly as the pattern it was ("I would...") — do not disguise a pattern as one event.
- Remember through all of these rules: a tidied-up transcript is a FAILURE. The rules above tell you what not to add; everything else — the arrangement, the pacing, the warmth, the flowing paragraphs — is your job to DO, fully.
- Add a short, evocative title on line 1

SHORT INPUT RULE: If the input is fewer than 30 words, expand it into the fullest, warmest
true version of their memory. Even a single sentence can become a beautiful short chapter.
${OUTPUT_RULES}

Their words:
"${text}"`;
  }

  // OWN-STORY (first-person, non-celebration) PATH -- F-364 ROLLBACK to the Whole Tale v4/v10 body
  // as it stood at 516f71b (2026-07-01), VERBATIM, plus today's two structural attachments
  // (griefKeepsakeNote + englishLanguageDirective). This replaces the v7 body that shipped
  // 2026-07-06 (0d7e971).
  //
  // WHY: founder blind read of nine cells (F-362) preferred this body on thin and dense material and
  // tied on middle; an independent external reader (F-363) chose it on both interviews she read. It is
  // the only historical version carrying BOTH "preserve their gold" (keep their most alive phrases
  // word-for-word) and "keep their maybes as maybes" (leave an unresolved feeling unresolved) -- the two
  // rules the v6->v9 line dropped. The F-362 candidate's extra ending rule was REJECTED by the same
  // blind read and is deliberately NOT carried here.
  //
  // celebrationGuardrail is not interpolated: this branch is only reached when !isCelebration, where it
  // resolved to the empty string, so the rendered prompt is byte-identical to 516f71b's own-story render.
  //
  // NOTE (F-364): context.plan is no longer read on this path, and the SHAPE-1 plan pass is no longer
  // CALLED either -- both call sites gate on shouldRunShapePlan(), which is false. (An earlier draft of
  // this comment said the pass "still RUNS"; it does not, and the row it cited never existed.)
  const OUTPUT_RULES = `
OUTPUT RULES -- NON-NEGOTIABLE:
- Write in the SAME LANGUAGE as the person's text. Never translate.
- Output ONLY the title on line 1, then the story text. Nothing else.
- Do NOT write anything before the title or after the last sentence of the story.
- Do NOT include notes, commentary, explanations, or metadata of any kind.
- Do NOT use "##" headers, "---" dividers, asterisks, or markdown formatting.
- Do NOT use em-dashes (the long dash). Use commas, periods, or colons instead. Ordinary hyphens in compound words are fine.
- No spoken scaffolding may survive in the output: never print phrases like "one specific thing that stands out," "um," "basically," "and then," "so yeah," or "pretty much."
- NEVER write the words "storyteller", "author", "contributor", or "the subject" in the title or story. The person is "I" (or, in a celebration/gift book, their name or natural pronouns: he, she, they). If the input text refers to "the storyteller", replace it the same way.
- If the input is very short, keep the output close to what they wrote -- fix grammar, add a title, but do not add invented detail.
- This text appears directly on a printed page in a family's keepsake book.${englishLanguageDirective(text)}`;

  return `${questionCtx}Please expand this rough draft into a warm, meaningful story in the first person, preserving the author's voice. ${wholeTaleLengthClause()}${griefKeepsakeNote(text)}

RULES:
- Preserve the author's voice and their specific words and phrases
- Keep all names, places, dates, and details exactly as stated
- Keep it in first person (third person for celebration/gift books)
- It's OK to expand rough notes into full, flowing paragraphs
- It's OK to add warmth, sensory detail, and gentle narrative structure
- It's OK to flesh out ideas they hinted at
- Do NOT invent facts, events, names, or dialogue they didn't mention
- Write in THEIR spoken voice: plain words this person could actually have said out loud. Do NOT add metaphors, poetic imagery, or reflective "wisdom" observations that are not theirs. If they do not speak in metaphor, the story does not either.
- Keep their most alive, personal phrases word-for-word. Never trade a vivid line of theirs for a smoother, more generic one.
- If they left a feeling, question, or hope unresolved, the story leaves it unresolved. Do not upgrade "I wondered if I could" into a lesson learned or a neat, triumphant ending.
- Never state a number, count, time, or amount they did not state. "My sisters" stays "my sisters", never "the three of us". "We played outside" does not become "we played outside until dark".
- FLOW MATTERS: spoken memories come out as short, clipped sentences. Combine closely related ones into smoother sentences of varied length using natural connectors (and, but, so, because, when). Keep their words; fix the flow.
- Their spoken generic "you" becomes first person: "your heart was racing" is written as "my heart was racing," "if you put your feet against the door" becomes "I would put my feet against the door." Keep the phrase, fix the pronoun.
- Find the story's turn before you write. It is often the line that undercuts or ends everything else they said ("I always got caught"), and often the first or funniest thing they said. NEVER drop that line: build the ending on it. BUT a flat sign-off ("Great." / "That's it" / "Yeah") is NOT the turn: dissolve it and end on their strongest material instead. If they described what usually happened rather than one specific time, keep it honestly as the pattern it was ("I would...") — do not disguise a pattern as one event.
- Remember through all of these rules: a tidied-up transcript is a FAILURE. The rules above tell you what not to add; everything else — the arrangement, the pacing, the warmth, the flowing paragraphs — is your job to DO, fully.
- Add a short, evocative title on line 1

SHORT INPUT RULE: If the input is fewer than 30 words, expand it into the fullest, warmest
true version of their memory. Even a single sentence can become a beautiful short chapter.
${OUTPUT_RULES}

Their words:
"${text}"`;
};

/**
 * SHAPE-1 PLAN pass (F-169): reads the assembled draft and NAMES the arc that already exists in the person's
 * own words -- the turn, the through-line, and the landing beat -- so the write pass (getWholePolishPrompt
 * with context.plan) can arrange their real material INTO that arc instead of trying to find-and-write the
 * shape in a single shot. Invents NOTHING: every item points to a real line already in the draft. This is
 * fidelity-safe arrangement, decoupled from dramatization (the invention-prone lever).
 *
 * @param {string} text  -- the assembled draft (buildAssemblerPrompt output)
 * @returns {string} a prompt whose output is a 3-line structural plan (TURN / THROUGH-LINE / LANDING)
 */
export const getWholeShapePlanPrompt = (text) => {
  return `You are a story editor. Below is a draft assembled from a person's own interview answers. Do NOT rewrite it and do NOT tell a story. Your ONLY job is to name the arc that ALREADY EXISTS inside their own words, so a writer can shape their real material into it.

Use ONLY what is in the draft. Invent nothing -- no new fact, line, feeling, image, or lesson. Every item you name must be a real line or moment already present in their words. If the draft is too thin to have a real turn or through-line, write "(too thin -- none stated)" for that item rather than inventing one. A short honest plan is correct; a padded one is a failure.

Draft:
"${text}"

Output EXACTLY these three lines and nothing else (a few words each, quoting or pointing to their real material):
TURN: the one real line everything pivots on -- often the funniest line, or the line that undercuts or ends everything else they said. Quote their words.
THROUGH-LINE: what this chapter is really about beneath the events -- their own "so what", drawn ONLY from what they said. Never a lesson you add.
LANDING: which real moment or image the chapter should END on. Point to the real line.

No prose, no preamble, no explanation. Just those three labeled lines.`;
};
